import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FileText, Download, ArrowLeft, Lock, CheckCircle2 } from 'lucide-react';
import {
  viewerApi,
  UnavailableError,
} from '../lib/viewer-api';
import type { ViewerContent, ViewerDoc, NdaPagesPayload } from '../lib/types';
import { DwellTracker, loadResumableVisit, saveVisit } from '../lib/tracker';

type Phase =
  | { kind: 'loading' }
  | { kind: 'unavailable' }
  | { kind: 'email' }
  | { kind: 'nda'; nda: NdaPagesPayload }
  | { kind: 'ready'; content: ViewerContent; signedCopyUrl?: string };

export default function ViewerPage() {
  const { slug = '' } = useParams();
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const [openDoc, setOpenDoc] = useState<ViewerDoc | null>(null);
  const trackerRef = useRef<DwellTracker | null>(null);
  const visitRef = useRef<{ visitId: string; startedAt: string } | null>(null);

  const becomeUnavailable = useCallback(() => {
    setPhase({ kind: 'unavailable' });
    setOpenDoc(null);
  }, []);

  // Bootstrap the gate state machine.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await viewerApi.bootstrap(slug);
        if (cancelled) return;
        if (res.state === 'email') setPhase({ kind: 'email' });
        else if (res.state === 'nda') setPhase({ kind: 'nda', nda: res.nda });
        else setPhase({ kind: 'ready', content: res.content });
      } catch (err) {
        if (!cancelled) {
          setPhase({ kind: 'unavailable' });
          if (!(err instanceof UnavailableError)) console.error(err);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Start (or resume) the visit + tracker once content is available.
  useEffect(() => {
    if (phase.kind !== 'ready') return;
    let cancelled = false;
    (async () => {
      try {
        let visit = loadResumableVisit(slug);
        if (!visit) {
          visit = await viewerApi.startVisit(slug);
          saveVisit(slug, visit);
        }
        if (cancelled) return;
        visitRef.current = visit;
        const tracker = new DwellTracker(slug, visit, becomeUnavailable);
        tracker.start();
        trackerRef.current = tracker;
      } catch (err) {
        if (err instanceof UnavailableError) becomeUnavailable();
      }
    })();
    return () => {
      cancelled = true;
      trackerRef.current?.stop();
      trackerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase.kind, slug]);

  // Refresh presigned URLs before they expire (also the revocation checkpoint).
  useEffect(() => {
    if (phase.kind !== 'ready') return;
    const expiresAt = new Date(phase.content.urlsExpireAt).getTime();
    const refreshIn = Math.max(60_000, expiresAt - Date.now() - 10 * 60_000);
    const t = setTimeout(async () => {
      try {
        const res = await viewerApi.content(slug);
        setPhase((p) =>
          p.kind === 'ready' ? { ...p, content: res.content } : p,
        );
      } catch (err) {
        if (err instanceof UnavailableError) becomeUnavailable();
      }
    }, refreshIn);
    return () => clearTimeout(t);
  }, [phase, slug, becomeUnavailable]);

  if (phase.kind === 'loading') {
    return (
      <Surface>
        <div className="animate-pulse text-mid">Loading…</div>
      </Surface>
    );
  }
  if (phase.kind === 'unavailable') {
    return (
      <Surface>
        <div className="text-center">
          <Lock className="mx-auto mb-4 h-10 w-10 text-soft" />
          <h1 className="font-heading text-xl font-semibold text-charcoal">
            This link is not available
          </h1>
          <p className="mt-2 text-sm text-mid">
            It may have been disabled or expired. Contact the sender for access.
          </p>
        </div>
      </Surface>
    );
  }
  if (phase.kind === 'email') {
    return <EmailGate slug={slug} onAdvance={setPhase} />;
  }
  if (phase.kind === 'nda') {
    return <NdaGate slug={slug} nda={phase.nda} onAdvance={setPhase} />;
  }

  if (openDoc) {
    return (
      <DocViewer
        slug={slug}
        doc={openDoc}
        allowDownload={phase.content.allowDownload}
        onBack={() => {
          trackerRef.current?.setCurrentPage(null);
          void trackerRef.current?.flush();
          setOpenDoc(null);
        }}
        tracker={trackerRef.current}
        visit={visitRef.current}
      />
    );
  }

  return (
    <DocList
      content={phase.content}
      signedCopyUrl={phase.signedCopyUrl}
      onOpen={setOpenDoc}
      onDownloadFile={async (d) => {
        try {
          const res = await viewerApi.download(
            slug,
            d.docId,
            visitRef.current?.visitId,
            visitRef.current?.startedAt,
          );
          window.location.href = res.url;
        } catch (err) {
          if (err instanceof UnavailableError) becomeUnavailable();
        }
      }}
    />
  );
}

function Surface({ children }: { children: React.ReactNode }) {
  return (
    <div className="viewer-surface flex min-h-full items-center justify-center p-6">
      {children}
    </div>
  );
}

// ──────── Email gate ────────

function EmailGate({
  slug,
  onAdvance,
}: {
  slug: string;
  onAdvance: (p: Phase) => void;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await viewerApi.identify(slug, name, email);
      if (res.state === 'nda') onAdvance({ kind: 'nda', nda: res.nda });
      else if (res.state === 'ready') onAdvance({ kind: 'ready', content: res.content });
    } catch (err) {
      if (err instanceof UnavailableError) onAdvance({ kind: 'unavailable' });
      else setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Surface>
      <form
        onSubmit={submit}
        className="w-full max-w-sm border border-line/50 bg-white p-8 shadow-xl"
      >
        <FileText className="mb-4 h-8 w-8 text-accent" />
        <h1 className="font-heading text-lg font-semibold text-ink">
          Enter your details to view
        </h1>
        <p className="mt-1 text-sm text-mid">
          The sender asks who is viewing these documents.
        </p>
        <label className="mt-5 block text-xs font-medium text-mid">
          Full name
          <input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 w-full border border-line px-3 py-2 text-sm text-ink focus:border-accent focus:outline-none"
            placeholder="Jane Investor"
          />
        </label>
        <label className="mt-3 block text-xs font-medium text-mid">
          Email
          <input
            required
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 w-full border border-line px-3 py-2 text-sm text-ink focus:border-accent focus:outline-none"
            placeholder="jane@fund.com"
          />
        </label>
        {error && <p className="mt-3 text-xs text-red-600">{error}</p>}
        <button
          disabled={busy}
          className="mt-5 w-full bg-accent py-2.5 text-sm font-semibold text-white hover:bg-accent-dark disabled:opacity-50"
        >
          {busy ? 'Checking…' : 'Continue'}
        </button>
      </form>
    </Surface>
  );
}

// ──────── NDA gate ────────

function NdaGate({
  slug,
  nda,
  onAdvance,
}: {
  slug: string;
  nda: NdaPagesPayload;
  onAdvance: (p: Phase) => void;
}) {
  const [agreed, setAgreed] = useState(false);
  const [typedName, setTypedName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sign = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await viewerApi.signNda(slug, typedName);
      if (res.state === 'ready') {
        onAdvance({ kind: 'ready', content: res.content, signedCopyUrl: res.signedCopyUrl });
      }
    } catch (err) {
      if (err instanceof UnavailableError) onAdvance({ kind: 'unavailable' });
      else setError(err instanceof Error ? err.message : 'Signing failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="viewer-surface min-h-full">
      <div className="mx-auto max-w-3xl px-4 py-8">
        <div className="border border-line/50 bg-white p-6 shadow-xl">
          <h1 className="font-heading text-lg font-semibold text-ink">
            Review and sign to continue
          </h1>
          <p className="mt-1 text-sm text-mid">
            The sender requires this agreement before you can view the documents.
          </p>
          <div className="mt-5 max-h-[55vh] overflow-y-auto border border-line/60 bg-paper p-3">
            {nda.pages.map((url, i) => (
              <img
                key={i}
                src={url}
                alt={`Agreement page ${i + 1}`}
                className="mx-auto mb-3 w-full max-w-2xl shadow-sm"
                loading={i < 2 ? 'eager' : 'lazy'}
              />
            ))}
          </div>
          <label className="mt-5 flex items-start gap-3 text-sm text-charcoal">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-accent"
            />
            <span>
              I have read the agreement above and consent to signing it
              electronically.
            </span>
          </label>
          <label className="mt-4 block text-xs font-medium text-mid">
            Type your full legal name as your signature
            <input
              value={typedName}
              onChange={(e) => setTypedName(e.target.value)}
              className="mt-1 w-full max-w-sm border border-line px-3 py-2 font-serif text-lg italic text-ink focus:border-accent focus:outline-none"
              placeholder="Jane Investor"
            />
          </label>
          {error && <p className="mt-3 text-xs text-red-600">{error}</p>}
          <button
            disabled={!agreed || !typedName.trim() || busy}
            onClick={sign}
            className="mt-5 bg-accent px-6 py-2.5 text-sm font-semibold text-white hover:bg-accent-dark disabled:opacity-40"
          >
            {busy ? 'Signing…' : 'Agree & sign'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ──────── Document list ────────

function formatSize(bytes: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function DocList({
  content,
  signedCopyUrl,
  onOpen,
  onDownloadFile,
}: {
  content: ViewerContent;
  signedCopyUrl?: string;
  onOpen: (d: ViewerDoc) => void;
  onDownloadFile: (d: ViewerDoc) => void;
}) {
  return (
    <div className="viewer-surface min-h-full">
      <div className="mx-auto max-w-4xl px-4 py-10">
        <h1 className="font-heading text-xl font-semibold text-charcoal">Shared documents</h1>
        {signedCopyUrl && (
          <a
            href={signedCopyUrl}
            className="mt-2 inline-flex items-center gap-1.5 text-sm text-accent hover:underline"
          >
            <CheckCircle2 className="h-4 w-4" /> Download your signed agreement
          </a>
        )}
        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
          {content.docs.map((d) =>
            d.downloadOnly ? (
              <button
                key={d.docId}
                onClick={() => onDownloadFile(d)}
                className="group overflow-hidden border border-line/50 bg-white text-left shadow transition hover:shadow-lg"
              >
                <div className="flex aspect-[3/4] flex-col items-center justify-center gap-2 bg-paper">
                  <FileText className="h-10 w-10 text-soft" />
                  <span className="flex items-center gap-1 rounded-full bg-accent px-3 py-1 text-xs font-semibold text-white group-hover:bg-accent-dark">
                    <Download className="h-3 w-3" /> Download
                  </span>
                </div>
                <div className="p-3">
                  <div className="truncate text-sm font-medium text-charcoal">
                    {d.title}
                  </div>
                  <div className="truncate text-xs text-mid">
                    {d.fileName} {d.sizeBytes ? `· ${formatSize(d.sizeBytes)}` : ''}
                  </div>
                </div>
              </button>
            ) : (
              <button
                key={d.docId}
                onClick={() => onOpen(d)}
                className="group overflow-hidden border border-line/50 bg-white text-left shadow transition hover:shadow-lg"
              >
                <div className="aspect-[3/4] overflow-hidden bg-paper">
                  <img
                    src={d.thumb ?? undefined}
                    alt=""
                    className="h-full w-full object-cover object-top transition group-hover:scale-[1.02]"
                  />
                </div>
                <div className="p-3">
                  <div className="truncate text-sm font-medium text-charcoal">
                    {d.title}
                  </div>
                  <div className="text-xs text-mid">{d.pageCount} pages</div>
                </div>
              </button>
            ),
          )}
        </div>
        {content.docs.length === 0 && (
          <p className="mt-10 text-sm text-mid">
            No documents are available yet.
          </p>
        )}
      </div>
    </div>
  );
}

// ──────── Document viewer with per-page tracking ────────

const pad = (n: number, w: number) => String(n).padStart(w, '0');

function DocViewer({
  slug,
  doc,
  allowDownload,
  onBack,
  tracker,
  visit,
}: {
  slug: string;
  doc: ViewerDoc;
  allowDownload: boolean;
  onBack: () => void;
  tracker: DwellTracker | null;
  visit: { visitId: string; startedAt: string } | null;
}) {
  const [currentPage, setCurrentPage] = useState(1);
  const containerRef = useRef<HTMLDivElement>(null);

  // IntersectionObserver picks the page nearest the viewport center; that page
  // is what the dwell timer attributes seconds to.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const pages = [...container.querySelectorAll('[data-page]')];
    const visible = new Map<number, number>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          const page = Number((e.target as HTMLElement).dataset.page);
          if (e.isIntersecting) visible.set(page, e.intersectionRatio);
          else visible.delete(page);
        }
        if (visible.size) {
          const best = [...visible.entries()].sort((a, b) => b[1] - a[1])[0][0];
          setCurrentPage(best);
          tracker?.setCurrentPage(`${doc.docId}#v${pad(doc.version, 3)}#p${pad(best, 4)}`);
        }
      },
      { threshold: [0.1, 0.3, 0.5, 0.7, 0.9] },
    );
    pages.forEach((p) => observer.observe(p));
    // Attribute the first page immediately (short docs may fire no callbacks
    // before the first tick).
    tracker?.setCurrentPage(`${doc.docId}#v${pad(doc.version, 3)}#p0001`);
    return () => {
      observer.disconnect();
      tracker?.setCurrentPage(null);
    };
  }, [doc, tracker]);

  const download = async () => {
    const res = await viewerApi.download(
      slug,
      doc.docId,
      visit?.visitId,
      visit?.startedAt,
    );
    window.location.href = res.url;
  };

  const jumpTo = (page: number) => {
    containerRef.current
      ?.querySelector(`[data-page="${page}"]`)
      ?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="viewer-surface flex h-full flex-col">
      <header className="flex items-center gap-3 border-b border-line bg-white px-4 py-2.5 shadow-sm">
        <button
          onClick={onBack}
          className="flex items-center gap-1 px-2 py-1 text-sm text-mid hover:bg-paper"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <div className="min-w-0 flex-1 truncate text-sm font-medium text-charcoal">
          {doc.title}
        </div>
        <div className="text-xs tabular-nums text-mid">
          {currentPage} / {doc.pageCount}
        </div>
        {allowDownload && (
          <button
            onClick={download}
            className="flex items-center gap-1.5 bg-accent px-3 py-1.5 text-xs font-semibold text-white hover:bg-accent-dark"
          >
            <Download className="h-3.5 w-3.5" /> Download
          </button>
        )}
      </header>
      <div className="flex min-h-0 flex-1">
        <nav className="hidden w-24 shrink-0 overflow-y-auto border-r border-line bg-paper p-2 sm:block">
          {doc.pages.map((url, i) => (
            <button
              key={i}
              onClick={() => jumpTo(i + 1)}
              className={`mb-2 block w-full overflow-hidden rounded border-2 ${
                currentPage === i + 1 ? 'border-accent' : 'border-transparent'
              }`}
            >
              <img src={url} alt="" loading="lazy" className="w-full" />
              <span className="block py-0.5 text-center text-[10px] text-mid">
                {i + 1}
              </span>
            </button>
          ))}
        </nav>
        <div ref={containerRef} className="min-w-0 flex-1 overflow-y-auto p-4">
          {doc.pages.map((url, i) => (
            <div key={i} data-page={i + 1} className="mx-auto mb-4 max-w-4xl">
              <img
                src={url}
                alt={`Page ${i + 1}`}
                loading={i < 3 ? 'eager' : 'lazy'}
                className="w-full bg-white shadow-md"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
